import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      // React-specific integrations
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0, // 20% in production, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Environment
    environment: import.meta.env.MODE,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION,

    // Always enabled when DSN is configured
    enabled: true,

    // Configure which URLs to trace
    tracePropagationTargets: ['localhost', /^https:\/\/api\.axetra\.example\.com/],

    // Before sending events
    beforeSend(event, hint) {
      // Filter out certain errors
      if (event.exception) {
        const error = hint.originalException;

        // Ignore network errors that are expected
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message);
          if (
            message.includes('Network Error') ||
            message.includes('Failed to fetch') ||
            message.includes('Load failed')
          ) {
            return null;
          }
        }
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Facebook errors
      'fb_xd_fragment',
      // Chrome extensions
      'chrome-extension://',
      // ResizeObserver loop errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: {
  id: number;
  email: string;
  name: string;
  role: string;
}) {
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.name,
  });

  // Set custom tags
  Sentry.setTag('user.role', user.role);
}

/**
 * Set account context for Sentry
 */
export function setSentryAccount(account: {
  id: number;
  name: string;
  status: string;
  plan?: string;
}) {
  Sentry.setContext('account', {
    id: account.id,
    name: account.name,
    status: account.status,
    plan: account.plan || 'none',
  });

  // Set custom tags
  Sentry.setTag('account.id', String(account.id));
  if (account.plan) {
    Sentry.setTag('subscription.plan', account.plan);
  }
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setContext('account', null);
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
}

/**
 * Manually capture a message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

